import React from 'react';
import { ContentBlock, Footer, LangBlock } from '../global';
import { Header } from '../header';
import { Loading } from '../loading';
import { observer } from 'mobx-react';
import { WebStorage } from '../../storage';
import type { AdminPage } from './admin_components/admin.utils';
import { useAdminData } from './admin_components/useAdminData';
import { AdminNav } from './admin_components/admin.ui';
import { AdminOverview } from './admin_components/admin.overview';
import { AdminAnalytics } from './admin_components/admin.analytics';
import { AdminUsers } from './admin_components/admin.users';
import { AdminOrders } from './admin_components/admin.orders';
import { AdminOrderManage } from './admin_components/admin.orders.manage';
import { AdminPayments } from './admin_components/admin.payments';
import { AdminPromo } from './admin_components/admin.promo';
import { AdminRewards } from './admin_components/admin.rewards';
import { AdminChangelog } from './admin_components/admin.changelog';
import { AdminNews } from './admin_components/admin.news';
import { AdminExtensions } from './admin_components/admin.extensions';
import { AdminApplication } from './admin_components/admin.application';
import { AdminLlm } from './admin_components/admin.llm';

export default observer(() => {
    const { userData } = WebStorage;
    const [, , LangString] = LangBlock();
    const [loaded, setLoaded] = React.useState(false);
    const [activePage, setActivePage] = React.useState<AdminPage>('overview');

    const adminData = useAdminData(loaded, !!userData?.admin);

    if (!userData?.admin) {
        return <></>;
    }

    const renderPage = () => {
        switch (activePage) {
            case 'overview':
                return (
                    <AdminOverview
                        diskSizeGB={adminData.diskSizeGB}
                        pausedAlert={adminData.pausedAlert}
                        loadAlert={adminData.loadAlert}
                    />
                );

            case 'analytics':
                return <AdminAnalytics />;

            case 'users':
                return (
                    <AdminUsers
                        usersList={adminData.usersList}
                        ordersList={adminData.ordersList}
                        loadUsersList={adminData.loadUsersList}
                    />
                );

            case 'orders':
                return (
                    <AdminOrders
                        ordersList={adminData.ordersList}
                        usersList={adminData.usersList}
                    />
                );

            case 'order-manage':
                return <AdminOrderManage />;

            case 'payments':
                return (
                    <AdminPayments
                        paymentsList={adminData.paymentsList}
                        usersList={adminData.usersList}
                    />
                );

            case 'promo':
                return (
                    <AdminPromo
                        promoList={adminData.promoList}
                        promoListUsed={adminData.promoListUsed}
                        usersList={adminData.usersList}
                        loadPromoList={adminData.loadPromoList}
                    />
                );

            case 'rewards':
                return <AdminRewards />;

            case 'changelog':
                return (
                    <AdminChangelog
                        changeLogs={adminData.changeLogs}
                        loadChangelogs={adminData.loadChangelogs}
                    />
                );

            case 'news':
                return <AdminNews news={adminData.news} loadNews={adminData.loadNews} />;

            case 'extensions':
                return (
                    <AdminExtensions
                        extensions={adminData.extensions}
                        loadExtensions={adminData.loadExtensions}
                    />
                );

            case 'application':
                return <AdminApplication />;

            case 'llm':
                return <AdminLlm />;

            default:
                return null;
        }
    };

    return (
        <div className="row admin">
            <Loading onLoaded={() => setLoaded(true)}>
                <Header />
                <ContentBlock lg={10} md={12}>
                    <div className="spaceUp admin-layout">
                        <aside className="admin-layout__nav">
                            <div className="admin-layout__brand">
                                <h1>{LangString('AdminPanel')}</h1>
                                <p>{LangString('AdminPanelDesc')}</p>
                            </div>
                            <AdminNav activePage={activePage} onSelect={setActivePage} />
                        </aside>
                        <main className="admin-layout__content">{renderPage()}</main>
                    </div>
                </ContentBlock>
                <Footer />
            </Loading>
        </div>
    );
});
